import axios from 'axios'

const jsonHeaders = { 'Content-Type': 'application/json' }

function requestConfig() {
  return { withCredentials: true, headers: jsonHeaders }
}

function formatRequestDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** Display date for instructor activation nudge status. */
export function formatInstructorNudgeDateTime(value) {
  if (!value) return null
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/**
 * @param {Record<string, unknown>} row
 */
export function mapInstructorNudgeStatusFromApi(row) {
  return {
    email: String(row.email ?? ''),
    userId: row.userId ?? null,
    hasOpened: Boolean(row.hasOpened),
    mostRecentlySentAt: row.mostRecentlySentAt ?? null,
    mostRecentOpenAt: row.mostRecentOpenAt ?? null,
  }
}

/**
 * @param {Record<string, unknown>} row
 */
export function mapInstructorRequestFromApi(row) {
  const id = row.id ?? row._id ?? row.requestId
  return {
    id,
    name: String(row.name ?? row.instructorName ?? ''),
    email: String(row.email ?? row.instructorEmail ?? ''),
    role: row.role ?? row.roles ?? null,
    type: row.type === 'remove' ? 'remove' : 'add',
    status: String(row.status ?? 'pending').toLowerCase(),
    date: formatRequestDate(row.createdAt ?? row.date ?? row.requestedAt) ?? '',
    responseDate: formatRequestDate(row.respondedAt ?? row.responseDate ?? null),
    respondedAt: row.respondedAt ?? row.responseDate ?? null,
    createdAt: row.createdAt ?? row.date ?? null,
    hasViewed: row.hasViewed === true || row.viewed === true || !!row.viewedAt || !!row.responseViewedAt,
  }
}

/**
 * @param {Record<string, unknown>} row
 */
export function mapCourseRequestFromApi(row) {
  const id = row.id ?? row._id ?? row.requestId
  const category = row.requestCategory ?? row.category ?? 'course'
  return {
    id,
    courseId: row.courseId != null ? Number(row.courseId) : null,
    courseName: String(row.courseName ?? row.name ?? 'Unknown'),
    fromCourse: row.fromCourse ?? row.fromCourseName ?? null,
    type: row.type === 'exchange' ? 'exchange' : 'add',
    status: String(row.status ?? 'pending').toLowerCase(),
    date: formatRequestDate(row.createdAt ?? row.date ?? row.requestedAt) ?? '',
    responseDate: formatRequestDate(row.respondedAt ?? row.responseDate ?? null),
    requestCategory: category === 'supplemental' ? 'supplemental' : 'course',
    respondedAt: row.respondedAt ?? row.responseDate ?? null,
    createdAt: row.createdAt ?? row.date ?? null,
    hasViewed: row.hasViewed === true || row.viewed === true || !!row.viewedAt || !!row.responseViewedAt,
  }
}

/**
 * @param {string} organizationId
 */
export async function fetchOrganizationInstructorRequests(organizationId) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/get-organization-instructor-requests`,
    { organizationId },
    requestConfig()
  )
  const rows = data?.requests ?? data?.instructorRequests ?? data ?? []
  return (Array.isArray(rows) ? rows : []).map(mapInstructorRequestFromApi)
}

/**
 * @param {string} organizationId
 */
export async function fetchOrganizationCourseRequests(organizationId) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/get-organization-course-requests`,
    { organizationId },
    requestConfig()
  )
  const rows = data?.requests ?? data?.courseRequests ?? data ?? []
  return (Array.isArray(rows) ? rows : []).map(mapCourseRequestFromApi)
}

/**
 * Submit instructor add or removal request for RefReps review.
 * @param {{
 *   organizationId: string,
 *   type: 'add' | 'remove',
 *   instructors?: Array<{ name: string, email: string, roles?: string, permissions?: Record<string, unknown> }>,
 *   instructor?: { instructorId?: string, name?: string, email?: string },
 *   requestedBy?: { userId?: string, name?: string, email?: string },
 * }} payload
 */
export async function postSendInstructorRequest(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/send-instructor-request`,
    payload,
    requestConfig()
  )
  return data
}

/**
 * Resend activation reminder email to a pending instructor.
 * @param {{ email: string, organization_name: string }} payload
 */
export async function postInstructorNudge(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/instructor-nudge`,
    payload,
    requestConfig()
  )
  return data
}

/**
 * Fetch activation email open / sent status for org instructors.
 * @param {string[]} emails
 */
export async function fetchInstructorNudgeStatusByEmails(emails) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/get-instructor-nudge-status-by-emails`,
    { emails },
    requestConfig()
  )
  return data
}

/**
 * Submit course or supplemental pack add / exchange request for RefReps review.
 * @param {{
 *   organizationId: string,
 *   type: 'add' | 'exchange',
 *   requestCategory?: 'course' | 'supplemental',
 *   courseIds?: number[],
 *   exchanges?: Array<{ fromCourseId: number | string, toCourseId: number | string }>,
 *   requestedBy?: { userId?: string, name?: string, email?: string },
 * }} payload
 */
export async function postSendCourseRequest(payload) {
  const { data } = await axios.post(
    `${import.meta.env.VITE_BASE_LINK}/send-course-request`,
    payload,
    requestConfig()
  )
  return data
}

/**
 * @param {string} organizationId
 * @param {{ userId?: string, name?: string, email?: string }} requestedBy
 */
export function buildRequestedBy(mainStore, requestedBy = {}) {
  const user = mainStore?.user
  const nameFromParts = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  return {
    userId: requestedBy.userId ?? user?.id ?? user?._id ?? null,
    name: requestedBy.name ?? (nameFromParts || user?.name || null),
    email: requestedBy.email ?? user?.email ?? null,
  }
}
